import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { axiosClient } from "@/lib/axiosClient";

export interface EventGuest {
    id: number;
    period_id: number;
    card_category_id: number;
    full_name: string;
    gender?: string | null;
    email?: string | null;
    phone_mobile?: string | null;
    institution?: string | null;
    position?: string | null;
    certificate_role?: string | null;
    blood_type?: string | null;
    clothing_size?: string | null;
    photo_path?: string | null;
    card_number?: string;
    rfid_number?: string | null;
    slug?: string;
    notes?: string | null;
    is_active: boolean;
    is_printed: boolean;
    is_emailed: boolean;
    print_batch?: number | null;
    printed_at?: string | null;
    import_batch?: string | null;
    created_at?: string;
    updated_at?: string;
    card_category?: {
        id: number;
        name: string;
        color_hex?: string | null;
        is_certificate_eligible?: boolean;
    } | null;
}

export type EventGuestPayload = Omit<EventGuest, "id" | "created_at" | "updated_at" | "card_category">;

export interface EventGuestFilters {
    period_id?: number | string;
    card_category_id?: number | string;
    gender?: string;
    blood_type?: string;
    clothing_size?: string;
    is_active?: string;
    is_printed?: string;
    is_emailed?: string;
    rfid_status?: string;
    print_batch?: string;
    /** Kelengkapan data email itu sendiri ("1" = punya email, "0" = belum ada email sama
     * sekali) - beda dari is_emailed yang mengecek status pengiriman email kartunya. */
    has_email?: string;
}

export interface EventGuestPrintBatch {
    print_batch: number;
    total: number;
    printed_at: string | null;
}

export interface EventGuestStats {
    total: number;
    printed: number;
    not_printed: number;
    with_rfid: number;
    emailed: number;
    no_email: number;
    per_category: { card_category_id: number; name: string; color_hex: string | null; total: number }[];
}

export const useEventGuests = (page = 1, search = "", perPage = 10, filters: EventGuestFilters = {}, sort: "newest" | "oldest" = "newest") => {
    return useQuery({
        queryKey: ["event-guests", page, search, perPage, filters, sort],
        queryFn: async () => {
            const { data } = await axiosClient.get("/event-guests", {
                params: { page, search, per_page: perPage, sort, ...filters },
                withCredentials: true,
            });
            return data.data;
        },
    });
};

export const useEventGuestStats = (search = "", filters: EventGuestFilters = {}) => {
    return useQuery<EventGuestStats>({
        queryKey: ["event-guests-stats", search, filters],
        queryFn: async () => {
            const { data } = await axiosClient.get("/event-guests/stats", {
                params: { search, ...filters },
                withCredentials: true,
            });
            return data.data;
        },
    });
};

export const useEventGuestPrintBatches = (periodId?: number | string) => {
    return useQuery<EventGuestPrintBatch[]>({
        queryKey: ["event-guests-print-batches", periodId],
        queryFn: async () => {
            const { data } = await axiosClient.get("/event-guests/print-batches", {
                params: periodId ? { period_id: periodId } : {},
                withCredentials: true,
            });
            return data;
        },
        enabled: !!periodId,
    });
};

export const useEventGuest = (id: number | null) => {
    return useQuery<{ success: boolean; data: EventGuest }>({
        queryKey: ["event-guest", id],
        queryFn: async () => {
            const { data } = await axiosClient.get(`/event-guests/${id}`, { withCredentials: true });
            return data;
        },
        enabled: !!id,
    });
};

export const useCreateEventGuest = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (payload: FormData) => {
            const { data } = await axiosClient.post("/event-guests", payload, {
                withCredentials: true,
                headers: { "Content-Type": "multipart/form-data" },
            });
            return data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["event-guests"] });
            queryClient.invalidateQueries({ queryKey: ["event-guests-stats"] });
        },
    });
};

export const useUpdateEventGuest = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async ({ id, payload }: { id: number; payload: FormData }) => {
            const { data } = await axiosClient.post(`/event-guests/${id}`, payload, {
                withCredentials: true,
                headers: { "Content-Type": "multipart/form-data" },
            });
            return data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["event-guests"] });
            queryClient.invalidateQueries({ queryKey: ["event-guests-stats"] });
        },
    });
};

export const useDeleteEventGuest = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (id: number) => {
            const { data } = await axiosClient.delete(`/event-guests/${id}`, { withCredentials: true });
            return data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["event-guests"] });
            queryClient.invalidateQueries({ queryKey: ["event-guests-stats"] });
        },
    });
};

export const useAssignGuestRfid = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async ({ id, rfidNumber }: { id: number; rfidNumber: string }) => {
            const { data } = await axiosClient.post(
                `/rfid/guest/assign/${id}`,
                { rfid_number: rfidNumber },
                { withCredentials: true }
            );
            return data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["event-guests"] });
        },
    });
};

export const useDeleteGuestRfid = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (id: number) => {
            const { data } = await axiosClient.delete(`/rfid/guest/delete/${id}`, { withCredentials: true });
            return data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["event-guests"] });
        },
    });
};
export const useToggleGuestPrinted = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (id: number) => {
            const { data } = await axiosClient.post(`/event-guests/${id}/toggle-printed`, {}, { withCredentials: true });
            return data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["event-guests"] });
            queryClient.invalidateQueries({ queryKey: ["event-guests-stats"] });
        },
    });
};
